import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  Paintbrush,
  Clock,
  CheckCircle,
  DollarSign,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Oczekuje', labelEn: 'Pending', color: 'bg-yellow-500/20 text-yellow-500' },
  quoted: { label: 'Wycenione', labelEn: 'Quoted', color: 'bg-blue-500/20 text-blue-500' },
  accepted: { label: 'Zaakceptowane', labelEn: 'Accepted', color: 'bg-green-500/20 text-green-500' },
  in_progress: { label: 'W realizacji', labelEn: 'In Progress', color: 'bg-purple-500/20 text-purple-500' },
  completed: { label: 'Ukończone', labelEn: 'Completed', color: 'bg-green-600/20 text-green-600' },
  cancelled: { label: 'Anulowane', labelEn: 'Cancelled', color: 'bg-red-500/20 text-red-500' }
};

export default function RetouchRequestsPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    price: '',
    estimated_days: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/retouch-requests`);
      setRequests(response.data.requests);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenQuoteModal = (request) => {
    setSelectedRequest(request);
    setQuoteForm({ price: '', estimated_days: '', notes: '' });
    setQuoteModalOpen(true);
  };

  const handleSendQuote = async () => {
    if (!quoteForm.price || !quoteForm.estimated_days) {
      toast.error(language === 'pl' ? 'Wypełnij cenę i czas realizacji' : 'Fill in price and estimated days');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/retouch-requests/${selectedRequest.id}/quote`, {
        request_id: selectedRequest.id,
        price: parseFloat(quoteForm.price),
        estimated_days: parseInt(quoteForm.estimated_days),
        notes: quoteForm.notes || null
      });
      toast.success(language === 'pl' ? 'Wycena wysłana!' : 'Quote sent!');
      setQuoteModalOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async (requestId) => {
    try {
      await axios.post(`${API}/retouch-requests/${requestId}/complete`);
      toast.success(language === 'pl' ? 'Oznaczono jako ukończone!' : 'Marked as completed!');
      fetchRequests();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/galleries" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">
              {language === 'pl' ? 'Prośby o retusz' : 'Retouch Requests'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'pl' ? 'Zarządzaj zamówieniami obróbki autorskiej' : 'Manage custom retouch orders'}
            </p>
          </div>
        </div>

        {requests.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Paintbrush className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">
                {language === 'pl' ? 'Brak próśb o retusz' : 'No retouch requests'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'pl' 
                  ? 'Gdy klient poprosi o obróbkę autorską, pojawi się tutaj' 
                  : 'When a client requests custom retouch, it will appear here'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request, idx) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="bg-card border-border">
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      {/* Photo thumbnail */}
                      {request.photo && (
                        <img
                          src={request.photo.thumbnail_url}
                          alt={request.photo.filename}
                          className="w-24 h-24 object-cover rounded-sm"
                        />
                      )}
                      
                      {/* Request info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold">{request.client_name}</h3>
                            <p className="text-sm text-muted-foreground">{request.client_email}</p>
                          </div>
                          <Badge className={STATUS_CONFIG[request.status]?.color}>
                            {language === 'pl' 
                              ? STATUS_CONFIG[request.status]?.label 
                              : STATUS_CONFIG[request.status]?.labelEn
                            }
                          </Badge>
                        </div>
                        
                        <p className="text-sm mb-4 p-3 bg-secondary/50 rounded-sm">
                          "{request.description}"
                        </p>
                        
                        {/* Quote info if exists */}
                        {request.quote_price && (
                          <div className="flex items-center gap-4 text-sm mb-4">
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {request.quote_price} zł
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {request.quote_days} {language === 'pl' ? 'dni' : 'days'}
                            </span>
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex gap-2">
                          {request.status === 'pending' && (
                            <Button 
                              size="sm"
                              onClick={() => handleOpenQuoteModal(request)}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              {language === 'pl' ? 'Wyślij wycenę' : 'Send Quote'}
                            </Button>
                          )}
                          {request.status === 'accepted' && (
                            <Button 
                              size="sm"
                              onClick={() => handleComplete(request.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              {language === 'pl' ? 'Oznacz jako ukończone' : 'Mark Complete'}
                            </Button>
                          )}
                          {request.status === 'quoted' && (
                            <Badge variant="outline">
                              {language === 'pl' ? 'Oczekuje na akceptację klienta' : 'Waiting for client acceptance'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Quote Modal */}
      <Dialog open={quoteModalOpen} onOpenChange={setQuoteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'pl' ? 'Wyślij wycenę' : 'Send Quote'}
            </DialogTitle>
            <DialogDescription>
              {language === 'pl' 
                ? 'Określ cenę i szacowany czas realizacji' 
                : 'Set the price and estimated delivery time'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'pl' ? 'Cena (PLN)' : 'Price (PLN)'}</Label>
              <Input
                type="number"
                value={quoteForm.price}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, price: e.target.value }))}
                placeholder="150"
                className="h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'pl' ? 'Szacowany czas (dni)' : 'Estimated days'}</Label>
              <Input
                type="number"
                value={quoteForm.estimated_days}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, estimated_days: e.target.value }))}
                placeholder="3"
                className="h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'pl' ? 'Notatki (opcjonalne)' : 'Notes (optional)'}</Label>
              <Textarea
                value={quoteForm.notes}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={language === 'pl' 
                  ? 'Dodatkowe informacje dla klienta...' 
                  : 'Additional information for the client...'
                }
                rows={3}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setQuoteModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button className="flex-1" onClick={handleSendQuote} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {language === 'pl' ? 'Wyślij' : 'Send'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
